import React, { useState, useEffect } from 'react'
import {
    Typography,
    TextField,
    Button,
    Breadcrumbs,
    Link,
    FormControlLabel,
    Switch,
    Chip,
    makeStyles,
    createStyles,
    ButtonGroup,
    Divider,
    Popover,
} from '@material-ui/core'
import TuneIcon from '@material-ui/icons/Tune'
import RefreshIcon from '@material-ui/icons/Refresh'
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined'
import { Theme, useTheme } from '@material-ui/core/styles'
import { Autocomplete } from '@mui/material'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Container } from '../../Container'
import { LoriConversationAnalysisController } from '../../../hooks/useLoriConversationAnalysis'
import { TrainingVideoController } from '../../../hooks/useTrainingVideo'
import { ProjectPlanController } from '../../../hooks/useProjectPlan'
import { AnalysisDashboard } from './AnalysisDashboard'
import { ConversationListView } from './ConversationListView'
import { ConversationDetailPanel } from './ConversationDetailPanel'
import { ProblemsView } from './ProblemsView'
import {
    AnalysisFilterOption,
    ConversationAnalyticsItem,
    ConversationProblem,
    ConversationType,
    formatLabel,
} from './analysisTypes'
import { Topic } from '../../../hooks/useTopics'

const CHANNEL_OPTIONS: { value: ConversationType; label: string }[] = [
    { value: 'APARTMENT_WEB', label: 'Web' },
    { value: 'APARTMENT_MOBILE', label: 'Mobile-Apartment' },
    { value: 'VENDOR_APP', label: 'Mobile-Vendor' },
]

const DATE_PRESETS = [
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'This Quarter', days: -1 },
    { label: 'All Time', days: 0 },
] as const

function getPresetDates(preset: typeof DATE_PRESETS[number]) {
    const now = new Date()
    if (preset.days === 0) return { start: null, end: null }
    if (preset.days === -1) {
        const quarterStart = new Date(
            now.getFullYear(),
            Math.floor(now.getMonth() / 3) * 3,
            1,
        )
        return {
            start: quarterStart.toISOString().split('T')[0],
            end: now.toISOString().split('T')[0],
        }
    }
    const start = new Date(now)
    start.setDate(start.getDate() - preset.days)
    return {
        start: start.toISOString().split('T')[0],
        end: now.toISOString().split('T')[0],
    }
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        filterDropdown: {
            width: 170,
            '& .MuiFormControl-root, .MuiInputBase-root': {
                height: '100%',
                color: theme.palette.text.primary,
            },
            '& .MuiInputBase-root': {
                paddingLeft: '12px',
            },
            '& .MuiButtonBase-root': {
                color: theme.palette.text.primary,
            },
            '& .MuiInput-underline:before, .MuiInput-underline:after, .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottom: `1px solid ${theme.palette.divider}`,
            },
        },
        datePicker: {
            width: 140,
            '& .MuiInputBase-root': {
                color: theme.palette.text.primary,
            },
        },
        channelPill: {
            borderRadius: 16,
            textTransform: 'none',
            minWidth: 0,
            padding: '4px 12px',
        },
    }),
)

interface Props {
    analyticsController: LoriConversationAnalysisController
    trainingVideoController: TrainingVideoController
    projectPlanController: ProjectPlanController
    topics: Topic[]
}

export const AnalysisTab = ({
    analyticsController,
    trainingVideoController,
    projectPlanController,
    topics,
}: Props) => {
    const theme = useTheme()
    const classes = useStyles()

    const {
        filters,
        drillPath,
        view,
        summary,
        summaryLoading,
        organizations,
        workspaces,
        users,
        drillInto,
        drillBack,
        showConversations,
        showDashboard,
        showProblems,
        updateFilter,
        resetFilters,
        // Problems
        problemFilters,
        problemListParams,
        problemsSummary,
        problemsSummaryLoading,
        problemsList,
        problemsListLoading,
        problemsByConversationId,
        selectedConversationId,
        setSelectedConversationId,
        updateProblemFilters,
        updateProblemListParams,
        createProblem,
        updateProblem,
        deleteProblem,
        createProblemLoading,
        updateProblemLoading,
        deleteProblemLoading,
    } = analyticsController

    const [
        selectedConversation,
        setSelectedConversation,
    ] = useState<ConversationAnalyticsItem | null>(null)

    const [
        selectedProblem,
        setSelectedProblem,
    ] = useState<ConversationProblem | null>(null)

    useEffect(() => {
        setSelectedConversationId(selectedConversation?.id ?? null)
    }, [selectedConversation, setSelectedConversationId])

    const problemDetailConversation: ConversationAnalyticsItem | null =
        selectedProblem?.conversation_detail ?? null

    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null)

    const allOrganization: AnalysisFilterOption = { id: -1, name: 'All' }
    const allWorkspace: AnalysisFilterOption = { id: -1, name: 'All' }
    const allUser: AnalysisFilterOption = { id: -1, name: 'All' }

    const organizationOptions = [allOrganization, ...organizations]
    const workspaceOptions = [allWorkspace, ...workspaces]
    const userOptions = [allUser, ...users]

    const selectedOrganization =
        organizationOptions.find((o) => o.id === filters.organizationId) ??
        allOrganization
    const selectedWorkspace =
        workspaceOptions.find((w) => w.id === filters.workspaceId) ??
        allWorkspace
    const selectedUser =
        userOptions.find((u) => u.id === filters.userId) ?? allUser

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                overflow: 'hidden',
                minWidth: 0,
                minHeight: 0,
            }}
        >
            {/* Filter Bar */}
            <Container
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing(1.5),
                    padding: theme.spacing(1),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                {/* Channel Pills */}
                <ButtonGroup size="small">
                    {CHANNEL_OPTIONS.map((ch) => (
                        <Button
                            key={ch.value}
                            className={classes.channelPill}
                            variant={
                                filters.conversationType === ch.value
                                    ? 'contained'
                                    : 'outlined'
                            }
                            color={
                                filters.conversationType === ch.value
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() =>
                                updateFilter({
                                    conversationType: ch.value,
                                })
                            }
                        >
                            {ch.label}
                        </Button>
                    ))}
                </ButtonGroup>

                {/* Organization */}
                <Autocomplete
                    disableClearable
                    options={organizationOptions}
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    className={classes.filterDropdown}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Organization"
                            label="Organization"
                        />
                    )}
                    value={selectedOrganization}
                    onChange={(_, newValue) => {
                        updateFilter({
                            organizationId:
                                newValue.id === -1 ? null : newValue.id,
                            workspaceId: null,
                            userId: null,
                        })
                    }}
                    size="small"
                />

                {/* Workspace */}
                <Autocomplete
                    disableClearable
                    options={workspaceOptions}
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    className={classes.filterDropdown}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Workspace"
                            label="Workspace"
                        />
                    )}
                    value={selectedWorkspace}
                    onChange={(_, newValue) => {
                        updateFilter({
                            workspaceId:
                                newValue.id === -1 ? null : newValue.id,
                        })
                    }}
                    size="small"
                />

                {/* User */}
                <Autocomplete
                    disableClearable
                    options={userOptions}
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    className={classes.filterDropdown}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="User"
                            label="User"
                        />
                    )}
                    value={selectedUser}
                    onChange={(_, newValue) => {
                        updateFilter({
                            userId: newValue.id === -1 ? null : newValue.id,
                        })
                    }}
                    size="small"
                />

                {/* Divider */}
                <Divider
                    orientation="vertical"
                    flexItem
                    style={{ margin: `0 ${theme.spacing(0.5)}px` }}
                />

                {/* Date Section */}
                <Container
                    style={{
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    {/* Quick date presets */}
                    <ButtonGroup size="small" variant="outlined">
                        {DATE_PRESETS.map((preset) => (
                            <Button
                                key={preset.label}
                                onClick={() => {
                                    const { start, end } = getPresetDates(
                                        preset,
                                    )
                                    updateFilter({
                                        startDate: start,
                                        endDate: end,
                                    })
                                }}
                                style={{ textTransform: 'none', fontSize: 11 }}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </ButtonGroup>

                    {/* Date pickers below presets */}
                    <Container
                        style={{
                            flexDirection: 'row',
                            gap: theme.spacing(1),
                        }}
                    >
                        <KeyboardDatePicker
                            className={classes.datePicker}
                            disableToolbar
                            variant="inline"
                            format="MM/dd/yyyy"
                            label="Start Date"
                            value={
                                filters.startDate
                                    ? new Date(filters.startDate + 'T00:00:00')
                                    : null
                            }
                            onChange={(date) => {
                                if (date && !isNaN(date.getTime())) {
                                    updateFilter({
                                        startDate: date
                                            .toISOString()
                                            .split('T')[0],
                                    })
                                } else if (!date) {
                                    updateFilter({ startDate: null })
                                }
                            }}
                            KeyboardButtonProps={{
                                'aria-label': 'change start date',
                            }}
                            autoOk
                        />
                        <KeyboardDatePicker
                            className={classes.datePicker}
                            disableToolbar
                            variant="inline"
                            format="MM/dd/yyyy"
                            label="End Date"
                            value={
                                filters.endDate
                                    ? new Date(filters.endDate + 'T00:00:00')
                                    : null
                            }
                            onChange={(date) => {
                                if (date && !isNaN(date.getTime())) {
                                    updateFilter({
                                        endDate: date
                                            .toISOString()
                                            .split('T')[0],
                                    })
                                } else if (!date) {
                                    updateFilter({ endDate: null })
                                }
                            }}
                            KeyboardButtonProps={{
                                'aria-label': 'change end date',
                            }}
                            autoOk
                        />
                    </Container>
                </Container>

                {/* Spacer to push actions to far right */}
                <Container style={{ flex: 1 }} />

                {/* More + Reset stacked */}
                <Container
                    style={{
                        flexDirection: 'column',
                        gap: 4,
                        alignItems: 'flex-end',
                    }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TuneIcon />}
                        onClick={(e) => setMoreAnchor(e.currentTarget)}
                        style={{ textTransform: 'none', minWidth: 100 }}
                    >
                        More
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={resetFilters}
                        style={{ textTransform: 'none', minWidth: 100 }}
                    >
                        Reset
                    </Button>
                </Container>
                <Popover
                    open={Boolean(moreAnchor)}
                    anchorEl={moreAnchor}
                    onClose={() => setMoreAnchor(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'column',
                            padding: theme.spacing(2),
                            gap: theme.spacing(1),
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={filters.excludeEzosUsers}
                                    onChange={(e) =>
                                        updateFilter({
                                            excludeEzosUsers: e.target.checked,
                                        })
                                    }
                                    color="primary"
                                    size="small"
                                />
                            }
                            label="Exclude Ezos Users"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={filters.requireUserMessages}
                                    onChange={(e) =>
                                        updateFilter({
                                            requireUserMessages:
                                                e.target.checked,
                                        })
                                    }
                                    color="primary"
                                    size="small"
                                />
                            }
                            label="Require User Messages"
                        />
                    </Container>
                </Popover>
            </Container>

            {/* Breadcrumbs + View Conversations button */}
            <Container
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
                }}
            >
                <Container
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <Breadcrumbs>
                        <Link
                            color="inherit"
                            href="#"
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault()
                                drillBack(0)
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            Analysis
                        </Link>
                        {drillPath.map((step, idx) => {
                            const isLast = idx === drillPath.length - 1
                            return isLast ? (
                                <Typography key={idx} color="textPrimary">
                                    {step.dimension === 'topic'
                                        ? 'Topic'
                                        : 'Intent'}
                                    : {step.label}
                                </Typography>
                            ) : (
                                <Link
                                    key={idx}
                                    color="inherit"
                                    href="#"
                                    onClick={(e: React.MouseEvent) => {
                                        e.preventDefault()
                                        drillBack(idx + 1)
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {step.dimension === 'topic'
                                        ? 'Topic'
                                        : 'Intent'}
                                    : {step.label}
                                </Link>
                            )
                        })}
                    </Breadcrumbs>

                    {/* Active filter chips */}
                    {filters.topic && drillPath.length === 0 && (
                        <Chip
                            label={`Topic: ${formatLabel(filters.topic)}`}
                            onDelete={() => updateFilter({ topic: null })}
                            size="small"
                        />
                    )}
                    {filters.intent && drillPath.length === 0 && (
                        <Chip
                            label={`Intent: ${formatLabel(filters.intent)}`}
                            onDelete={() => updateFilter({ intent: null })}
                            size="small"
                        />
                    )}
                </Container>

                <Container
                    style={{
                        flexDirection: 'row',
                        gap: theme.spacing(1),
                    }}
                >
                    {(view === 'conversations' || view === 'problems') && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={showDashboard}
                        >
                            Back to Dashboard
                        </Button>
                    )}
                    {view === 'dashboard' && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={showConversations}
                        >
                            View Conversations
                        </Button>
                    )}
                    {view !== 'problems' && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FlagOutlinedIcon />}
                            onClick={showProblems}
                            style={{ textTransform: 'none' }}
                        >
                            Problems
                        </Button>
                    )}
                </Container>
            </Container>

            {/* Main Content */}
            <Container
                style={{
                    flex: 1,
                    overflow: 'auto',
                    minHeight: 0,
                    minWidth: 0,
                }}
            >
                {view === 'dashboard' && (
                    <AnalysisDashboard
                        summary={summary}
                        loading={summaryLoading}
                        drillInto={drillInto}
                        drillPath={drillPath}
                        groupByTime={filters.groupByTime}
                        onGroupByTimeChange={(value: 'month' | 'week') =>
                            updateFilter({ groupByTime: value })
                        }
                    />
                )}
                {view === 'conversations' && (
                    <Container
                        style={{
                            flex: 1,
                            flexDirection: 'row',
                        }}
                    >
                        <ConversationListView
                            analyticsController={analyticsController}
                            selectedConversation={selectedConversation}
                            onSelectConversation={setSelectedConversation}
                            topics={topics}
                            problemsByConversationId={problemsByConversationId}
                            onCreateProblem={createProblem}
                            onUpdateProblem={updateProblem}
                            onDeleteProblem={deleteProblem}
                            problemSaving={
                                createProblemLoading ||
                                updateProblemLoading ||
                                deleteProblemLoading
                            }
                        />
                        <ConversationDetailPanel
                            conversation={selectedConversation}
                            trainingVideoController={trainingVideoController}
                            projectPlanController={projectPlanController}
                            conversationProblem={
                                selectedConversation
                                    ? problemsByConversationId.get(
                                          selectedConversation.id,
                                      ) ?? null
                                    : null
                            }
                        />
                    </Container>
                )}
                {view === 'problems' && (
                    <Container
                        style={{
                            flex: 1,
                            flexDirection: 'row',
                        }}
                    >
                        <ProblemsView
                            summary={problemsSummary}
                            summaryLoading={problemsSummaryLoading}
                            problemsList={problemsList}
                            problemsListLoading={problemsListLoading}
                            problemFilters={problemFilters}
                            problemListParams={problemListParams}
                            onUpdateFilters={updateProblemFilters}
                            onUpdateListParams={updateProblemListParams}
                            selectedProblem={selectedProblem}
                            onSelectProblem={setSelectedProblem}
                        />
                        <ConversationDetailPanel
                            conversation={problemDetailConversation}
                            trainingVideoController={trainingVideoController}
                            projectPlanController={projectPlanController}
                        />
                    </Container>
                )}
            </Container>
        </Container>
    )
}
